<template>
  <div class="landing">
    <!-- Hero -->
    <section class="hero">
      <div class="hero-inner">
        <p class="hero-eyebrow">Broche Ballet</p>
        <h1 class="hero-title">En Pointe Program</h1>
        <p class="hero-subtitle">
          A twice-annual cohort for adult ballet dancers ready to begin or return to pointework —
          guided by a Foot 360 assessment and personalized practice plan.
        </p>

        <div v-if="loadingCohort" class="cohort-loading">
          <v-progress-circular indeterminate color="#1a1a1a" size="24"></v-progress-circular>
        </div>

        <!-- Open cohort -->
        <div v-else-if="activeCohort" class="cohort-open">
          <div class="cohort-card">
            <p class="cohort-label">Applications Open</p>
            <h2 class="cohort-name">{{ activeCohort.name }}</h2>
            <p class="cohort-dates">
              Closes {{ formatDate(activeCohort.closeDate) }}
            </p>
            <div class="cohort-fees">
              <div class="fee-item">
                <span class="fee-label">Application Fee</span>
                <span class="fee-amount">${{ activeCohort.applicationFee }}</span>
              </div>
              <div class="fee-item">
                <span class="fee-label">Cohort Fee (if approved)</span>
                <span class="fee-amount">${{ activeCohort.cohortFee }}</span>
              </div>
            </div>
            <router-link to="/pointe/apply" class="btn-apply">Apply Now →</router-link>
          </div>
        </div>

        <!-- No open cohort — show waitlist -->
        <div v-else class="cohort-closed">
          <div class="closed-card">
            <p class="closed-label">Applications are currently closed</p>
            <p class="closed-text">
              We open applications twice a year. Join the waitlist to be notified when the next cohort opens.
            </p>
            <div class="convertkit-embed" ref="convertkitEmbed"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- What is this -->
    <section class="about-section">
      <div class="section-inner">
        <h2 class="section-title">What is the En Pointe Program?</h2>
        <div class="about-columns">
          <div class="about-col">
            <h3>Foot 360 Consultation</h3>
            <p>
              Before applying to the cohort, every dancer undergoes a thorough Foot 360 Assessment.
              We evaluate your feet, ankles, strength, and alignment against our en pointe criteria —
              and give you a personalized practice plan to help you get there.
            </p>
          </div>
          <div class="about-col">
            <h3>The Cohort Experience</h3>
            <p>
              Approved dancers join a small cohort of adult ballet dancers for structured weekly
              support as you begin your pointe journey. You're never alone in this process.
            </p>
          </div>
          <div class="about-col">
            <h3>Ready for Pointe?</h3>
            <p>
              Not sure where you stand? Browse all of our en pointe criteria and resource links
              on the self-assessment page — no application needed.
            </p>
            <router-link to="/pointe/self-assessment" class="link-cta">Explore Self Assessment →</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Process -->
    <section class="process-section">
      <div class="section-inner">
        <h2 class="section-title">The Application Process</h2>
        <div class="steps">
          <div class="step">
            <div class="step-num">1</div>
            <div class="step-body">
              <h4>Apply & Pay Application Fee</h4>
              <p>Complete the application questionnaire and submit your application fee to open your spot.</p>
            </div>
          </div>
          <div class="step">
            <div class="step-num">2</div>
            <div class="step-body">
              <h4>Submit Your Video</h4>
              <p>Upload a short video showing your feet and ankles. We use this to assess your readiness.</p>
            </div>
          </div>
          <div class="step">
            <div class="step-num">3</div>
            <div class="step-body">
              <h4>Receive Your Results</h4>
              <p>We review your video against all criteria and send you detailed feedback within 2 weeks.</p>
            </div>
          </div>
          <div class="step">
            <div class="step-num">4</div>
            <div class="step-body">
              <h4>Join the Cohort or Keep Working</h4>
              <p>Approved dancers join the cohort. If not ready yet, your fee can be applied toward a Foot 360 Consultation.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA footer -->
    <section class="cta-section">
      <div class="section-inner">
        <h2>Not sure if you're ready?</h2>
        <p>Browse our full criteria list and resources — then decide when the time feels right.</p>
        <div class="cta-buttons">
          <router-link to="/pointe/self-assessment" class="btn-outline">Explore Self Assessment</router-link>
          <router-link v-if="activeCohort" to="/pointe/apply" class="btn-dark">Apply Now</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { db } from '@/firebase'

export default {
  data () {
    return {
      activeCohort: null,
      loadingCohort: true
    }
  },
  async created () {
    this.$store.commit('setLayout', 'pointe-layout')
    const snap = await db.collection('pointe-cohorts')
      .where('status', '==', 'open')
      .limit(1)
      .get()
    this.activeCohort = snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() }
    this.loadingCohort = false
    if (!this.activeCohort) {
      this.$nextTick(() => this.injectConvertKit('convertkitEmbed'))
    }
  },
  methods: {
    injectConvertKit (ref) {
      const container = this.$refs[ref]
      if (!container) return
      const script = document.createElement('script')
      script.async = true
      script.setAttribute('data-uid', '0166d3e2a8')
      script.src = 'https://broche-ballet.kit.com/0166d3e2a8/index.js'
      container.appendChild(script)
    },
    formatDate (ts) {
      if (!ts) return ''
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    }
  }
}
</script>

<style scoped>
.landing {
  font-family: 'Fanwood Text', Georgia, serif;
}

/* Hero */
.hero {
  background: #1a1a1a;
  color: #e8e5e1;
  padding: 80px 24px;
}

.hero-inner {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.hero-eyebrow {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: .14em;
  color: #999;
  margin: 0 0 12px;
}

.hero-title {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 52px;
  font-weight: 700;
  color: #e8e5e1;
  margin: 0 0 20px;
  line-height: 1.1;
}

.hero-subtitle {
  font-size: 19px;
  color: #ccc;
  line-height: 1.7;
  max-width: 580px;
  margin: 0 auto 48px;
}

.cohort-loading { margin: 48px auto; text-align: center; }

/* Open cohort card */
.cohort-card {
  background: #fff;
  color: #1a1a1a;
  border-radius: 4px;
  padding: 40px;
  max-width: 460px;
  margin: 0 auto;
  text-align: left;
}

.cohort-label {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .1em;
  color: #2e7d32;
  margin: 0 0 8px;
}

.cohort-name {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 28px;
  margin: 0 0 8px;
}

.cohort-dates { font-size: 15px; color: #888; margin: 0 0 24px; }

.cohort-fees { display: flex; flex-direction: column; gap: 8px; margin-bottom: 28px; }

.fee-item { display: flex; justify-content: space-between; align-items: center; }

.fee-label { font-size: 14px; color: #666; }
.fee-amount { font-size: 18px; font-weight: 600; }

.btn-apply {
  display: inline-block;
  background: #1a1a1a;
  color: #fff;
  text-decoration: none;
  padding: 14px 32px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 16px;
  border-radius: 2px;
  transition: background 0.2s;
  width: 100%;
  text-align: center;
  box-sizing: border-box;
}

.btn-apply:hover { background: #333; }

/* Closed */
.closed-card {
  background: rgba(255,255,255,.08);
  border: 1px solid rgba(255,255,255,.15);
  border-radius: 4px;
  padding: 40px;
  max-width: 520px;
  margin: 0 auto;
}

.closed-label {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 18px;
  color: #e8e5e1;
  margin: 0 0 12px;
}

.closed-text { font-size: 15px; color: #aaa; line-height: 1.6; margin: 0 0 24px; }

.convertkit-embed { margin-top: 16px; }

/* About */
.about-section { background: #fff; padding: 72px 24px; }

.section-inner { max-width: 1100px; margin: 0 auto; }

.section-title {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 34px;
  color: #1a1a1a;
  margin: 0 0 40px;
  text-align: center;
}

.about-columns { display: grid; grid-template-columns: repeat(3, 1fr); gap: 40px; }

.about-col h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 20px;
  color: #1a1a1a;
  margin: 0 0 12px;
}

.about-col p { font-size: 16px; line-height: 1.7; color: #555; }

.link-cta {
  display: inline-block;
  margin-top: 12px;
  font-size: 14px;
  color: #1a1a1a;
  text-decoration: underline;
}

/* Process */
.process-section { background: #faf9f7; padding: 72px 24px; }

.steps { display: flex; flex-direction: column; gap: 24px; max-width: 700px; margin: 0 auto; }

.step { display: flex; gap: 24px; align-items: flex-start; }

.step-num {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #1a1a1a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
}

.step-body h4 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 18px;
  margin: 4px 0 6px;
  color: #1a1a1a;
}

.step-body p { font-size: 15px; color: #666; line-height: 1.6; margin: 0; }

/* CTA */
.cta-section { background: #e8e5e1; padding: 72px 24px; text-align: center; }

.cta-section h2 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 30px;
  color: #1a1a1a;
  margin: 0 0 12px;
}

.cta-section p { font-size: 16px; color: #555; margin: 0 0 32px; }

.cta-buttons { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }

.btn-outline {
  display: inline-block;
  border: 2px solid #1a1a1a;
  color: #1a1a1a;
  padding: 12px 28px;
  text-decoration: none;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 16px;
  border-radius: 2px;
  transition: background 0.2s, color 0.2s;
}

.btn-outline:hover { background: #1a1a1a; color: #fff; }

.btn-dark {
  display: inline-block;
  background: #1a1a1a;
  color: #fff;
  padding: 12px 28px;
  text-decoration: none;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 16px;
  border-radius: 2px;
}

.btn-dark:hover { background: #333; }

@media (max-width: 768px) {
  .hero-title { font-size: 36px; }
  .about-columns { grid-template-columns: 1fr; }
  .cohort-card { padding: 28px 20px; }
}
</style>
